import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { RelativeTime } from '@/components/ui/relative-time'
import { getInitials } from '@/lib/utils'
import type { Message } from '@/types/conversations'
import { MessageAttachments } from '../MessageAttachments'
import { MousePointerClick, Undo2 } from 'lucide-react'

interface UserMessageProps {
  message: Message
  isLastUserMessage?: boolean
  canUndoMessageId?: number | null
  isRewinding?: boolean
  onUndo?: () => void
  isOrphaned?: boolean
}

export const UserMessage = memo(function UserMessage({
  message,
  isLastUserMessage = false,
  canUndoMessageId,
  isRewinding = false,
  onUndo,
  isOrphaned = false
}: UserMessageProps) {
  const { t } = useTranslation('conversations')
  const userName = message.user?.name || 'Anonymous User'
  const avatarUrl = message.user?.avatar_url
  const initials = getInitials(userName)

  // Check if this message can be undone
  const canUndoThisMessage = canUndoMessageId === message.id
  const [isHovered, setIsHovered] = useState(false)

  // Helper function to generate user-friendly display text for selected element
  const getElementDisplayText = (context: NonNullable<typeof message.selected_element_context>) => {
    if (context.source === 'canvas') {
      const els = context.elements || []
      if (els.length === 1) {
        const el = els[0]
        return el.text
          ? (el.text.length > 50 ? el.text.slice(0, 47) + '...' : el.text)
          : el.type
      }
      return `${els.length} elements`
    }
    const text = context.text?.trim()
    if (text && text.length > 0) {
      return text.length > 50 ? `${text.slice(0, 47)}...` : text
    }
    return `<${context.tag}>`
  }

  return (
    <div
      className={`flex justify-end gap-3 animate-in fade-in slide-in-from-right-2 duration-300 min-w-0 mt-4 ${isOrphaned ? 'opacity-50' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col items-end gap-1 max-w-[80%] min-w-0">
        <div className="bg-slate-200 dark:text-foreground dark:bg-slate-800 border border-primary/10 rounded-lg px-3 py-2 min-w-0">
          {message.selected_element_context && (message.selected_element_context.selector || message.selected_element_context.source === 'canvas') && (
            <div className="mb-2">
              <Badge variant="secondary" className="gap-1.5 py-1 px-2">
                <MousePointerClick className="h-3 w-3" />
                <span className="text-[11px] max-w-[250px] truncate" title={
                  message.selected_element_context.source === 'canvas'
                    ? `${message.selected_element_context.elements?.length} canvas element(s)`
                    : t('chat.technicalSelector', { selector: message.selected_element_context.selector })
                }>
                  {getElementDisplayText(message.selected_element_context)}
                </span>
              </Badge>
            </div>
          )}
          <p className="text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">{message.content}</p>
          <MessageAttachments attachments={message.attachments} />
        </div>
        <div className="flex items-center gap-2">
          {isLastUserMessage && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 px-2 text-muted-foreground hover:text-foreground transition-opacity ${
                    canUndoThisMessage && !isRewinding && isHovered
                      ? 'opacity-100'
                      : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  {t('chat.undo')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('chat.undoTitle')}</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2">
                      <p>{t('chat.undoDescription')}</p>
                      <p className="text-amber-600 dark:text-amber-500 font-medium">
                        {t('history.rewind.confirmWarning')}
                      </p>
                      <p className="text-amber-600 dark:text-amber-500">
                        {t('history.rewind.confirmNote')}
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('chat.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={onUndo}>{t('chat.undo')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <RelativeTime
            timestamp={message.created_at}
            className="text-xs text-muted-foreground"
          />
          {/* TODO: Re-enable cost display in the future */}
          {/* {message.cost_usd !== undefined && (
            <span className="text-xs font-medium text-red-600 dark:text-red-400 px-2 py-0.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded">
              ${message.cost_usd.toFixed(4)} USD
            </span>
          )} */}
        </div>
      </div>
      <Avatar className="h-7 w-7 flex-shrink-0">
        <AvatarImage src={avatarUrl || undefined} alt={userName} />
        <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium border border-primary/20">
          {initials}
        </AvatarFallback>
      </Avatar>
    </div>
  )
})
